// plugins/pwa.client.ts

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare global {
  interface Window {
    deferredPwaPrompt?: BeforeInstallPromptEvent | null;
  }
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  // Skip this plugin entirely on server side
  if (process.server) {
    return {
      provide: {
        pwa: {
          canInstall: ref(false),
          installApp: () => Promise.resolve(),
          isInstalled: ref(false),
        },
      },
    };
  }

  const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null);
  const isInstalled = ref(false);
  const promptChecked = ref(false);

  // Check if app is already installed
  const checkIfInstalled = () => {
    if (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches ||
      window.matchMedia('(display-mode: minimal-ui)').matches ||
      (navigator as any).standalone === true
    ) {
      isInstalled.value = true;
      deferredPrompt.value = null;
      window.deferredPwaPrompt = null;
    }
    promptChecked.value = true;
  };

  const handleBeforeInstallPrompt = (event: BeforeInstallPromptEvent) => {
    if (!isInstalled.value) {
      event.preventDefault();
      window.deferredPwaPrompt = event;
      deferredPrompt.value = event;
      promptChecked.value = true;
    }
  };

  const handleAppInstalled = () => {
    deferredPrompt.value = null;
    window.deferredPwaPrompt = null;
    isInstalled.value = true;
  };

  const handleDisplayModeChange = (e: MediaQueryListEvent) => {
    if (e.matches) {
      isInstalled.value = true;
      deferredPrompt.value = null;
      window.deferredPwaPrompt = null;
    }
  };

  // Calculate canInstall
  const canInstall = computed(
    () =>
      Boolean(deferredPrompt.value) &&
      !isInstalled.value &&
      promptChecked.value,
  );

  const installApp = async () => {
    if (!deferredPrompt.value) return;

    const promptEvent = deferredPrompt.value as BeforeInstallPromptEvent;
    promptEvent.prompt();

    const choiceResult = await promptEvent.userChoice;

    if (choiceResult.outcome === 'accepted') {
      isInstalled.value = true;
    }

    deferredPrompt.value = null;
    window.deferredPwaPrompt = null;
  };

  // Only run setup after the app is mounted
  nuxtApp.hook('app:mounted', () => {
    // Check if it's already installed
    checkIfInstalled();

    // If not installed, check for existing prompt
    if (!isInstalled.value && window.deferredPwaPrompt) {
      deferredPrompt.value = window.deferredPwaPrompt;
      promptChecked.value = true;
    }

    // Add event listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Listen for display mode changes
    window
      .matchMedia('(display-mode: standalone)')
      .addEventListener('change', handleDisplayModeChange);
    window
      .matchMedia('(display-mode: fullscreen)')
      .addEventListener('change', handleDisplayModeChange);
    window
      .matchMedia('(display-mode: minimal-ui)')
      .addEventListener('change', handleDisplayModeChange);
  });

  // Clean up event listeners when the app is unmounted
  nuxtApp.hook('app:unmounted', () => {
    window.removeEventListener(
      'beforeinstallprompt',
      handleBeforeInstallPrompt,
    );
    window.removeEventListener('appinstalled', handleAppInstalled);

    window
      .matchMedia('(display-mode: standalone)')
      .removeEventListener('change', handleDisplayModeChange);
    window
      .matchMedia('(display-mode: fullscreen)')
      .removeEventListener('change', handleDisplayModeChange);
    window
      .matchMedia('(display-mode: minimal-ui)')
      .removeEventListener('change', handleDisplayModeChange);
  });

  // Provide the PWA utility functions to your components
  return {
    provide: {
      pwa: {
        canInstall,
        installApp,
        isInstalled,
      },
    },
  };
});
